import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import { authRequestCode, authVerifyCode } from '../lib/api';
import { useAuth } from '../store/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful login */
  onLoginSuccess?: () => void;
}

type Step = 'email' | 'code' | 'success';

export default function LoginModal({ isOpen, onClose, onLoginSuccess }: LoginModalProps) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep('email');
      setEmail('');
      setCode('');
      setError('');
      setLoading(false);
      setTimeout(() => emailRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Focus code input when step changes
  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => codeRef.current?.focus(), 100);
    }
  }, [step]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setLoading(true);
    try {
      await authRequestCode(email.trim().toLowerCase());
      setStep('code');
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      setError(msg || 'Er is iets misgegaan. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError('');
    setLoading(true);
    try {
      const { user } = await authVerifyCode(email.trim().toLowerCase(), code.trim());
      setUser(user);
      setUserName(user.name);
      setStep('success');
      setTimeout(() => {
        onLoginSuccess?.();
        onClose();
        // ─── Role-based redirect ──────────────────────────────────────────
        // Owner / superadmin → admin dashboard (was: bleef op homepage)
        // Reseller            → reseller dashboard
        // Customer            → customer dashboard (alleen als nog niet
        //                       op een nuttige pagina, anders blijven)
        const r = user.role;
        const onLandingPage =
          window.location.pathname === '/' ||
          window.location.pathname === '/login' ||
          window.location.pathname === '/register';
        if (r === 'owner' || r === 'superadmin') {
          navigate('/admin');
        } else if (r === 'reseller') {
          navigate('/reseller-dashboard');
        } else if (r === 'customer' && onLandingPage) {
          navigate('/dashboard');
        }
      }, 1800);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      setError(msg || 'Ongeldige code. Controleer uw e-mail en probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-[#1a1a1a] px-6 py-5 flex items-center justify-between">
          <div>
            <div className="w-7 h-7 bg-[#c47a3a] rounded-md flex items-center justify-center mb-2">
              <span className="text-white font-bold text-sm">G</span>
            </div>
            <h2 className="text-white font-bold text-base leading-tight">
              {step === 'success' ? 'Welkom terug!' : 'Inloggen'}
            </h2>
            <p className="text-gray-400 text-xs mt-0.5">
              {step === 'email' && 'Voer uw e-mailadres in'}
              {step === 'code' && `Code verstuurd naar ${email}`}
              {step === 'success' && `Ingelogd als ${userName}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            aria-label="Sluiten"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#4a4a4a] mb-1.5 uppercase tracking-wide">
                  E-mailadres
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                  <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="naam@bedrijf.be"
                    required
                    className="w-full pl-9 pr-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] focus:ring-1 focus:ring-[#c47a3a]/30 transition-colors"
                  />
                </div>
              </div>

              {error && (
                <p className="text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-2.5 bg-[#c47a3a] text-white font-semibold rounded-lg text-sm hover:bg-[#b06a2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? 'Versturen…' : 'Stuur code'}
              </button>

              <p className="text-center text-[11px] text-[#8a7e74]">
                Nog geen account? De code werkt ook voor nieuwe gebruikers.
              </p>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#4a4a4a] mb-1.5 uppercase tracking-wide">
                  6-cijferige code
                </label>
                <div className="relative">
                  <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                  <input
                    ref={codeRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                    placeholder="123456"
                    required
                    className="w-full pl-9 pr-3 py-2.5 bg-[#faf7f3] border border-gray-200 rounded-lg text-sm text-center tracking-widest font-mono focus:outline-none focus:border-[#c47a3a] focus:ring-1 focus:ring-[#c47a3a]/30 transition-colors"
                  />
                </div>
                <p className="text-[11px] text-[#8a7e74] mt-1.5">
                  Controleer uw inbox (en spammap).
                </p>
              </div>

              {error && (
                <p className="text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full py-2.5 bg-[#c47a3a] text-white font-semibold rounded-lg text-sm hover:bg-[#b06a2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? 'Verifiëren…' : 'Inloggen'}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(''); }}
                className="w-full py-2 text-[#8a7e74] text-xs hover:text-[#4a4a4a] transition-colors"
              >
                Ander e-mailadres gebruiken
              </button>
            </form>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center py-4 gap-3">
              <CheckCircle2 size={40} className="text-green-500" />
              <p className="text-sm font-semibold text-[#1a1a1a]">Succesvol ingelogd!</p>
              <p className="text-xs text-[#8a7e74]">Welkom, {userName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
